import {
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  HostListener,
} from '@angular/core';

@Component({
  selector: 'app-image-360-viewer',
  templateUrl: './image-360-viewer.component.html',
  styleUrls: ['./image-360-viewer.component.scss'],
})
export class Image360ViewerComponent implements OnInit, OnDestroy {
  @Input() images: string[] = [];
  @Input() productName: string = 'Producto';
  @Input() disableModal: boolean = false;

  currentFrame = 0;
  isDragging = false;
  isLoading = true;
  isInteractive = false;
  loadedImages = 0;
  isModalOpen = false;
  hasDragged = false;
  loadingPercentage = 0;
  loadingMessage = 'Preparando vista 360°...';
  isFullyLoadedCache = false;

  private lastMouseX = 0;
  private lastTouchX = 0;
  private dragSensitivity = 3;

  /** Solo frames decodificados OK (no contar errores). */
  private imageCache = new Map<number, HTMLImageElement>();
  private maxConcurrentLoads = 8;
  private readonly interactiveThreshold = 12;
  private destroyed = false;

  constructor(private zone: NgZone, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.preloadProgressive();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.imageCache.clear();
  }

  /**
   * 1) Frame 0 al instante → se muestra el producto
   * 2) Frames prioritarios → se habilita el arrastre
   * 3) Resto en background
   */
  private async preloadProgressive(): Promise<void> {
    if (!this.images?.length) {
      this.isLoading = false;
      this.loadingMessage = 'No hay imágenes disponibles';
      return;
    }

    const total = this.images.length;
    this.loadingMessage = 'Cargando vista 360°...';

    try {
      await this.loadImage(0);
      this.unlockPreview();

      const priority = this.buildPriorityIndexes(total);
      await this.loadIndexes(priority);

      this.unlockInteractive();

      const remaining: number[] = [];
      for (let i = 0; i < total; i++) {
        if (!this.imageCache.has(i)) remaining.push(i);
      }
      this.loadIndexes(remaining).then(() => {
        if (this.destroyed) return;
        this.zone.run(() => {
          this.isFullyLoadedCache = true;
          this.loadingPercentage = 100;
          this.loadingMessage = 'Listo';
          this.cdr.markForCheck();
        });
      });
    } catch (e) {
      console.error('Error al cargar imágenes 360:', e);
      this.unlockInteractive();
    }
  }

  private buildPriorityIndexes(total: number): number[] {
    const set = new Set<number>([0]);
    const neighbors = Math.min(this.interactiveThreshold, total);
    for (let i = 1; i < neighbors; i++) set.add(i);

    const samples = Math.min(16, total);
    for (let s = 0; s < samples; s++) {
      set.add(Math.floor((s * total) / samples));
    }
    return Array.from(set).sort((a, b) => a - b);
  }

  private async loadIndexes(indexes: number[]): Promise<void> {
    const pending = indexes.filter((i) => !this.imageCache.has(i));
    for (let i = 0; i < pending.length; i += this.maxConcurrentLoads) {
      if (this.destroyed) return;
      const batch = pending.slice(i, i + this.maxConcurrentLoads);
      await Promise.all(batch.map((idx) => this.loadImage(idx)));
    }
  }

  private loadImage(frameIndex: number): Promise<void> {
    if (this.imageCache.has(frameIndex)) {
      return Promise.resolve();
    }
    if (frameIndex < 0 || frameIndex >= this.images.length) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const img = new Image();
      img.decoding = 'async';

      const finishOk = () => {
        if (this.destroyed) {
          resolve();
          return;
        }
        this.imageCache.set(frameIndex, img);
        this.zone.run(() => {
          this.loadedImages = this.imageCache.size;
          this.loadingPercentage = Math.round(
            (this.imageCache.size / this.images.length) * 100
          );
          this.cdr.markForCheck();
        });
        resolve();
      };

      // Error: no marcar como cargado (evita saltar a frames rotos al girar)
      const finishErr = () => resolve();

      img.onload = () => {
        if (img.decode) {
          img.decode().then(finishOk).catch(finishOk);
        } else {
          finishOk();
        }
      };
      img.onerror = finishErr;
      img.src = this.images[frameIndex];
    });
  }

  private unlockPreview(): void {
    this.zone.run(() => {
      this.isLoading = false;
      this.loadingMessage = 'Preparando rotación...';
      this.cdr.markForCheck();
    });
  }

  private unlockInteractive(): void {
    this.zone.run(() => {
      this.isLoading = false;
      this.isInteractive = this.imageCache.size > 0;
      this.isFullyLoadedCache = this.imageCache.size >= this.images.length;
      this.loadingMessage = this.isFullyLoadedCache
        ? 'Listo'
        : 'Cargando el resto...';
      this.cdr.markForCheck();
    });
  }

  onMouseDown(event: MouseEvent): void {
    if (this.isLoading || !this.isInteractive) return;
    this.isDragging = true;
    this.hasDragged = false;
    this.lastMouseX = event.clientX;
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging || !this.isInteractive) return;
    const deltaX = event.clientX - this.lastMouseX;
    if (Math.abs(deltaX) > 5) this.hasDragged = true;
    this.lastMouseX = event.clientX;
    this.updateFrame(deltaX);
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    const wasDragging = this.isDragging;
    const didDrag = this.hasDragged;
    this.isDragging = false;
    if (
      wasDragging &&
      !didDrag &&
      !this.disableModal &&
      this.isInteractive
    ) {
      this.openModal();
    }
  }

  onClick(event: MouseEvent): void {
    if (!this.hasDragged && !this.disableModal && this.isInteractive) {
      this.openModal();
    }
    event.stopPropagation();
  }

  onTouchStart(event: TouchEvent): void {
    if (this.isLoading || !this.isInteractive) return;
    this.isDragging = true;
    this.hasDragged = false;
    this.lastTouchX = event.touches[0].clientX;
  }

  onTouchMove(event: TouchEvent): void {
    if (!this.isDragging || !this.isInteractive) return;
    const deltaX = event.touches[0].clientX - this.lastTouchX;
    if (Math.abs(deltaX) > 5) this.hasDragged = true;
    this.lastTouchX = event.touches[0].clientX;
    this.updateFrame(deltaX);
    event.preventDefault();
  }

  onTouchEnd(): void {
    const wasDragging = this.isDragging;
    const didDrag = this.hasDragged;
    this.isDragging = false;
    if (
      wasDragging &&
      !didDrag &&
      !this.disableModal &&
      this.isInteractive
    ) {
      this.openModal();
    }
  }

  private updateFrame(deltaX: number): void {
    const frameChange = Math.floor(deltaX / this.dragSensitivity);
    if (frameChange === 0) return;

    let newFrame = this.currentFrame - frameChange;
    const len = this.images.length;
    newFrame = ((newFrame % len) + len) % len;

    if (!this.imageCache.has(newFrame)) {
      newFrame = this.nearestLoadedFrame(newFrame);
    }

    if (newFrame === this.currentFrame) return;
    this.currentFrame = newFrame;
    this.cdr.markForCheck();
  }

  private nearestLoadedFrame(target: number): number {
    if (this.imageCache.has(target)) return target;
    const len = this.images.length;
    for (let d = 1; d < len; d++) {
      const a = (target + d) % len;
      const b = (target - d + len) % len;
      if (this.imageCache.has(a)) return a;
      if (this.imageCache.has(b)) return b;
    }
    return this.imageCache.has(0) ? 0 : this.currentFrame;
  }

  openModal(): void {
    if (!this.disableModal && this.isInteractive) {
      this.isModalOpen = true;
    }
  }

  closeModal(): void {
    this.isModalOpen = false;
  }

  get currentImage(): string {
    const frame = this.imageCache.has(this.currentFrame)
      ? this.currentFrame
      : this.nearestLoadedFrame(this.currentFrame);
    return this.images[frame] || this.images[0] || '';
  }

  get loadingProgress(): number {
    if (!this.images?.length) return 0;
    return Math.round((this.loadedImages / this.images.length) * 100);
  }

  get isFullyLoaded(): boolean {
    return this.loadedImages >= this.images.length;
  }

  get showBackgroundProgress(): boolean {
    return this.isInteractive && !this.isFullyLoadedCache;
  }
}
